import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import PostCard from '../components/PostCard';
import { Loader2, Send } from 'lucide-react';

export default function PostPage() {
  const { postId } = useParams();
  const { currentUser } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commenting, setCommenting] = useState(false);

  const fetchComments = async () => {
    const commentsQuery = query(collection(db, 'posts', postId, 'comments'), orderBy('createdAt', 'asc'));
    const snapshot = await getDocs(commentsQuery);
    setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      const postRef = doc(db, 'posts', postId);
      const [docSnap] = await Promise.all([getDoc(postRef), fetchComments()]);
      if (docSnap.exists()) {
        setPost({ id: docSnap.id, ...docSnap.data() });
      } else {
        setError('Post not found.');
      }
      setLoading(false);
    };
    fetchPost();
  }, [postId]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser) return;
    setCommenting(true);
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        text: newComment,
        authorId: currentUser.uid,
        authorEmail: currentUser.email,
        createdAt: serverTimestamp(),
      });
      setNewComment('');
      await fetchComments();
    } catch (err) {
      console.error('Error adding comment: ', err);
    }
    setCommenting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 glass-card rounded-xl">
        <h3 className="text-xl font-semibold text-white">{error}</h3>
        <Link to="/" className="text-cyan-400 hover:text-cyan-300 mt-4 inline-block">Go back to Home</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {post && <PostCard post={post} />}

      <div className="glass-card rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Comments ({comments.length})</h2>
        
        {currentUser && (
          <form onSubmit={handleCommentSubmit} className="flex gap-4 items-start mb-8">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add your comment..."
              className="w-full glass-card rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              rows="3"
            />
            <button type="submit" disabled={commenting || !newComment.trim()} className="glow-button glass-card p-4 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed">
              {commenting ? <Loader2 className="animate-spin" size={24} /> : <Send size={24} />}
            </button>
          </form>
        )}

        <div className="space-y-6">
          {comments.map(comment => (
            <div key={comment.id} className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 flex items-center justify-center font-bold text-white">
                {comment.authorEmail ? comment.authorEmail.substring(0, 1).toUpperCase() : 'A'}
              </div>
              <div className="flex-grow glass-card p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-bold text-purple-300">{comment.authorEmail?.split('@')[0]}</p>
                  <p className="text-xs text-gray-500">
                    {comment.createdAt ? new Date(comment.createdAt.toDate()).toLocaleString() : 'Just now'}
                  </p>
                </div>
                <p className="text-gray-300 whitespace-pre-wrap">{comment.text}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && (
            <p className="text-center text-gray-500 py-8">No comments yet. Be the first to chime in!</p>
          )}
        </div>
      </div>
    </div>
  );
}